import { MapViewerInitOptions, MapViewerService, Viewer3dInitOptions, Viewer3dService } from '@3ddv/ngx-dvm-internal';
import { inject, Injectable, isDevMode } from '@angular/core';
import { MapViewerLoadMapOptions, MapViewerLoadObject, Viewer3dLoadObject, Viewer3dLoadView3dOptions } from '@3ddv/dvm-internal';
import { BehaviorSubject, map, Observable, switchMap, tap } from 'rxjs';
import { SiteConfiguration } from '../configuration/configuration';
import { VIEWER, VIEWER3D, MINIMAP } from '../dvm.providers';

@Injectable({
  providedIn: 'root'
})
export class ViewersService {
  private venueOptions: { viewer: { venueId: string, mapId: string }, minimap: { mapId: string } } = SiteConfiguration.map.dvm; // { viewer: { venueId: 'eu-ch-00005-fclugano', mapId: 'blockmap' }, minimap: { mapId: blockmap_minimap } };

  // DVM CONFIGURATION  
  public readonly viewer: MapViewerService = inject(VIEWER);
  public readonly viewer3d: Viewer3dService = inject(VIEWER3D);
  public readonly minimap: MapViewerService = inject(MINIMAP);
  // VIEWERS SERVICES
  // public readonly viewer: MapViewerService = inject(MapViewerService);
  // public readonly viewer3d: Viewer3dService = inject(Viewer3dService);
  // public readonly minimap: MapViewerService = inject(MapViewerService);


  // VIEWERS INIT CONFIGURATION
  private viewerInitOptions: MapViewerInitOptions = {};
  private viewer3dInitOptions: Viewer3dInitOptions = {};
  private minimapInitOptions: MapViewerInitOptions = {};


  // VIEWERS LOAD CONFIGURATION
  private viewerLoadOptions: MapViewerLoadMapOptions = { venue_id: '', map_id: '' };
  private viewer3dLoadOptions: Viewer3dLoadView3dOptions = { venue_id: '', view_id: '' };
  private minimapLoadOptions: MapViewerLoadMapOptions = { venue_id: '', map_id: '' };

  // STATUS
  public currentMap: BehaviorSubject<'topview' | 'seatmap'> = new BehaviorSubject<'topview' | 'seatmap'>('topview');
  // public currentView: BehaviorSubject<'topview' | 'seatmap' | 'view3d'> = new BehaviorSubject<'topview' | 'seatmap' | 'view3d'>('topview');
  public is3dView: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // constructor(
  //   public readonly viewer: MapViewerService,
  //   public readonly viewer3d: Viewer3dService
  // ) { }

  // MAIN INIT
  public initViewers(): void {

    this.initMapViewer(); // Init 2D viewer

    this.initViewer3d();  // Init 3D viewer

    this.initMinimap();   // Init minimap

  }

  //INIT 3d VIEWER
  public initVr(): void {

    this.initViewer3d();  // Init 3D viewer

  }


  //#region MAP VIEWER METHODS

  // PUBLIC METHODS
  public loadMap(mapId?: string, type?: string, availability?: string[]): Observable<MapViewerLoadObject> {
    const options: MapViewerLoadMapOptions = mapId ? Object.assign({}, this.viewerLoadOptions, { map_id: mapId }) : this.viewerLoadOptions;
    // console.log('Load map: ', options);

    return this.viewer.loadMap(options).pipe(
      tap(() => {
        // Set availability
        this.setAvailability(type, availability);
        // Set the current map view (to know the flow state of the viewport)
        const isTopview: boolean = options.map_id === this.venueOptions.viewer.mapId;
        const currentMap: 'topview' | 'seatmap' = isTopview ? 'topview' : 'seatmap';
        this.setCurrentMap(currentMap);

        // Minimap goTo
        this.minimapNavigation(options.map_id!);
      })
    );
  }

  public setAvailability(type?: string, availability?: string[]): void {
    if (type && availability) {
      this.viewer.setAvailability(type, availability);
    } else {
      this.viewer.getTypesList().forEach((t) => this.viewer.setAvailability(type ?? t, availability ?? this.viewer.getNodesByType(type ?? t)));
    }
  }

  /**
   * Set the current view to the viewer.
   * @returns void
   */
  private setCurrentMap(currentView: 'topview' | 'seatmap'): void {
    this.currentMap.next(currentView);
  }

  public navigateToSection(sectionId: string): Observable<MapViewerLoadObject | Viewer3dLoadObject> {
    const options: MapViewerLoadMapOptions = sectionId ? Object.assign({}, this.viewerLoadOptions, { map_id: sectionId }) : this.viewerLoadOptions;

    return this.viewer.hasMap(options).pipe(
      switchMap((hasMap: boolean) => hasMap ? this.loadMap(sectionId, 'seat') : this.load3dView(sectionId))
    );
  }

  // PRIVATE METHODS
  private initMapViewer(): void {
    if (!this.viewer.isInitialized()) {
      this.setViewerInitOptions(); // Set the viewer Init Options

      this.viewer.initialize(this.viewerInitOptions).subscribe((): void => {
        this.setViewerFlags(); // Set the viewer Flags
        this.setViewerLoadOptions(); // Set the viewer Load Options
        this.loadMap().subscribe(); // Load the map
        this.addDvmDebugger(); // Add the DVM to the window to be able to debug the viewer
      });

    } else {
      this.setViewerLoadOptions(); // Set the viewer Load Options
      this.loadMap().subscribe(); // Load the map
    }
  }

  private setViewerInitOptions(): void {
    this.viewerInitOptions = { plugins: ['navigable_minimap'] };
  }

  private setViewerFlags(): void {
    this.viewer.flags.fixed_aspect_ratio = false;
    this.viewer.flags.automatic_selection = false;
  }

  private setViewerLoadOptions(): void {
    this.viewerLoadOptions = { venue_id: this.venueOptions.viewer.venueId, map_id: this.venueOptions.viewer.mapId };
  }

  //#endregion


  //#region VIEWER 3D METHODS

  // PUBLIC METHODS
  public load3dView(viewId: string): Observable<Viewer3dLoadObject> {
    const options: Viewer3dLoadView3dOptions = Object.assign({}, this.viewer3dLoadOptions, { view_id: viewId });
    this.toggle3dView(true);
    return this.viewer3d.loadView3d(options);
    // return this.viewer3d.loadView3d(options).pipe(tap((): void => this.setCurrentView()));
  }

  public close3dView(): void {
    this.toggle3dView(false);
    this.viewer3d.reset();

    // this.setCurrentView();
  }

  // PRIVATE METHODS
  private initViewer3d(): void {
    if (this.viewer.isInitialized()) return;
    // this.setViewer3dInitOptions(); // Set the viewer 3D Init Options

    this.viewer3d.initialize(this.viewer3dInitOptions).subscribe((): void => {
      this.setViewer3dFlags(); // Set the viewer 3D Flags
      this.setViewer3dLoadOptions(); // Set the viewer 3D Load Options
    });
  }


  // private setViewer3dInitOptions(): void {
  //   this.viewer3dInitOptions = {};
  // }

  private setViewer3dFlags(): void {
    this.viewer3d.flags.fixed_aspect_ratio = false;
    this.viewer3d.flags.show_fullscreen_button = false;
  }

  private setViewer3dLoadOptions(): void {
    this.viewer3dLoadOptions = { venue_id: this.venueOptions.viewer.venueId, view_id: '' };
  }

  private toggle3dView(show: boolean): void {
    if (show !== this.is3dView.value) {
      this.is3dView.next(show);
    }
  }

  //#endregion


  //#region MINIMAP METHODS

  // PUBLIC METHODS

  // PRIVATE METHODS
  private initMinimap(): void {
    if (!this.viewer.isInitialized()) {

      this.minimap.initialize(this.minimapInitOptions).subscribe((): void => {
        this.setMinimapFlags(); // Set the minimap Flags
        this.setMinimapLoadOptions(); // Set the minimap Load Options
        this.minimap.loadMap(this.minimapLoadOptions).subscribe((): void => {
          this.setMinimapAvailability();
        });
      });

    } else {
      this.setMinimapLoadOptions(); // Set the minimap Load Options
      this.minimap.loadMap(this.minimapLoadOptions).subscribe((): void => {
        this.setMinimapAvailability();
      });

    };
  }

  private setMinimapFlags(): void {
    this.minimap.flags.fixed_aspect_ratio = false;
    this.minimap.max_selection = 1;
  }

  private setMinimapLoadOptions(): void {
    // this.minimapLoadOptions = { venue_id: 'nam-us-00036-lakers-premium', map_id: this.venueOptions.minimap.mapId };
    this.minimapLoadOptions = { venue_id: this.venueOptions.viewer.venueId, map_id: this.venueOptions.minimap.mapId };
  }

  private setMinimapAvailability(type?: string, availability?: string[]): void {
    if (type && availability) {
      this.minimap.setAvailability(type, availability)
    } else {
      this.minimap.getTypesList().forEach((t) => this.minimap.setAvailability(type ?? t, availability ?? this.minimap.getNodesByType(type ?? t)));
    }
  }

  private minimapNavigation(mapId: string): void {
    if (this.viewerLoadOptions.map_id !== mapId) {
      this.minimap.unselectAll();
      this.minimap.select(mapId);
      this.minimap.goTo(mapId).subscribe({
        error: error => {
          console.error('Error navigating to minimap: ', error);
        }
      });
    }
    else {
      this.minimap.unselectAll();
      if (this.minimap.limits?.bbox_limits) {
        this.minimap.goTo(this.minimap.limits.bbox_limits);
      }
    }
  }

  //#endregion


  //#region DVM UTILS

  /**
   * Add the DVM to the window to be able to debug the viewer.
   * @returns void
   */
  private addDvmDebugger(): void {
    if (!isDevMode()) return;

    (window as { viewer?: MapViewerService }).viewer = this.viewer;
    (window as { viewer3d?: Viewer3dService }).viewer3d = this.viewer3d;
    (window as { minimap?: MapViewerService }).minimap = this.minimap;
  }
  //#endregion

}
